import { t } from '@lingui/core/macro';

import { useHealthCenter } from '@/api/healthCenter';
import AreaIcon from '@/assets/svg/bx-area.svg?react';
import BarChartIcon from '@/assets/svg/bx-bar-chart-alt.svg?react';
import BuildingIcon from '@/assets/svg/bx-buildings-alt.svg?react';
import CodeIcon from '@/assets/svg/bx-code-alt.svg?react';
import DollarCircleIcon from '@/assets/svg/bx-dollar-circle.svg?react';
import LinkIcon from '@/assets/svg/bx-link.svg?react';
import MapPinIcon from '@/assets/svg/bx-map-pin.svg?react';
import TrendingUpIcon from '@/assets/svg/bx-trending-up.svg?react';
import UserAddIcon from '@/assets/svg/bx-user-plus.svg?react';
import { Page } from '@/components/page';
import { Panel } from '@/components/panels';
import { formatDate, formatDateFromNow } from '@/helper/dateHelper';
import { ROUTES } from '@/router';

import Fireworks from './ConfettiCanvas';
import HealthPod from './HealthPod';
import HealthPodDetail from './HealthPodDetails';
import HealthPodUser from './HealthPodUser';
import OnboardingProgressBar from './OnboardingProgressBar';

const HealthCenter = () => {
  const { healthCenterData, isLoading } = useHealthCenter();

  return (
    <>
      <Page title={t`Health Center`} className="flex flex-1 flex-col gap-6" exampleAIQuestions={[]}>
        <Panel className="overflow-hidden" title={t`Onboarding Progress and Status`} isRounded>
          <OnboardingProgressBar
            isLoading={isLoading && !healthCenterData?.onboardingProgress}
            value={healthCenterData?.onboardingProgress}
          />
        </Panel>
        <div className="relative grid grid-cols-[repeat(auto-fit,minmax(530px,1fr))] gap-8">
          <HealthPod
            data={healthCenterData?.tag}
            isLoading={isLoading}
            icon={CodeIcon}
            title={t`Channel99 Tag`}
            titleInfoPopover={{
              title: t`Channel99 Tag`,
              body: t`Channel99 Tag will indicate "Incomplete" if we do not receive a pageview within 24 hours.`,
            }}
            instructions={t`Place Channel99 Tag on your website`}
            link={ROUTES.companySettings.path}
          >
            <HealthPodDetail label={t`Installed`}>
              {formatDate(healthCenterData?.tag?.tagInstalledAt, 'MM/DD/YYYY')}
            </HealthPodDetail>
            <HealthPodDetail label={t`Last Data Received`}>
              {formatDate(healthCenterData?.tag?.lastEventAt, 'MM/DD/YYYY')}
            </HealthPodDetail>
          </HealthPod>
          <HealthPod
            data={healthCenterData?.tam}
            isLoading={isLoading}
            icon={MapPinIcon}
            title={t`Total Addressable Market`}
            instructions={t`Define your organization's TAM (Total Addressable Market)`}
            link={ROUTES.companySettings.path}
          >
            <HealthPodDetail label={t`Last Updated`}>
              {formatDate(healthCenterData?.tam?.updatedAt, 'MM/DD/YYYY')}
            </HealthPodDetail>
            <HealthPodDetail label={t`By`}>
              <HealthPodUser user={healthCenterData?.tam?.updatedBy} />
            </HealthPodDetail>
          </HealthPod>
          <HealthPod
            data={healthCenterData?.crm}
            isLoading={isLoading}
            icon={BarChartIcon}
            title={t`CRM`}
            instructions={t`Connect your CRM for pipeline data and audience creation`}
            link={ROUTES.integrationByCategory('crm')}
          >
            <HealthPodDetail label={t`Last Sync`}>
              {formatDateFromNow(healthCenterData?.crm?.lastSynced)}
            </HealthPodDetail>
          </HealthPod>
          <HealthPod
            data={{ label: '', isAvailable: true, isComplete: false, isFree: true }}
            title={t`Ad Vendors`}
            isLoading={isLoading}
            icon={LinkIcon}
            instructions={t`Automated Spend and Metrics from Ad Vendors`}
            link={ROUTES.integrationByCategory('advertising')}
          />
          <HealthPod
            data={healthCenterData?.channelMapping}
            isLoading={isLoading}
            icon={TrendingUpIcon}
            title={t`Channel Mapping`}
            instructions={t`Review Channel Mapping`}
            link={ROUTES.configureChannels.path}
          >
            <HealthPodDetail label={t`Last Update`}>
              {formatDate(healthCenterData?.channelMapping?.updatedAt, 'MM/DD/YYYY')}
            </HealthPodDetail>
            <HealthPodDetail label={t`By`}>
              <HealthPodUser user={healthCenterData?.channelMapping?.updatedBy} />
            </HealthPodDetail>
          </HealthPod>
          <HealthPod
            data={healthCenterData?.vendorSpend}
            isLoading={isLoading}
            icon={DollarCircleIcon}
            title={t`Vendor Spend`}
            instructions={t`Keep Vendor Spend up to date`}
            link={ROUTES.configureVendors.path}
          >
            <HealthPodDetail label={t`Spend Input Through`}>
              {formatDate(healthCenterData?.vendorSpend?.spendInputThroughAt, 'MM/YYYY')}
            </HealthPodDetail>
            {healthCenterData?.vendorSpend?.lastUpdatedBy != null && (
              <HealthPodDetail label={t`By`}>
                <HealthPodUser user={healthCenterData?.vendorSpend?.lastUpdatedBy} />
              </HealthPodDetail>
            )}
          </HealthPod>
          <HealthPod
            data={healthCenterData?.customAudience}
            isLoading={isLoading}
            icon={BuildingIcon}
            title={t`Custom Audience`}
            instructions={t`Create Custom Audience for more precise insights`}
            link={ROUTES.audiences.path}
          >
            <HealthPodDetail label={t`Last Created`}>
              {formatDate(healthCenterData?.customAudience?.createdAt, 'MM/DD/YYYY')}
            </HealthPodDetail>
            <HealthPodDetail label={t`By`}>
              <HealthPodUser user={healthCenterData?.customAudience?.createdBy} />
            </HealthPodDetail>
          </HealthPod>
          <HealthPod
            data={healthCenterData?.userAdmin}
            isLoading={isLoading}
            icon={UserAddIcon}
            title={t`Users and Admins`}
            instructions={t`Invite Users and Admins to the C99 Platform`}
            link={ROUTES.users.path}
          >
            <HealthPodDetail label={t`Last User Added`}>
              {formatDate(healthCenterData?.userAdmin?.lastUserAddedAt, 'MM/DD/YYYY')}
            </HealthPodDetail>
          </HealthPod>
          <HealthPod
            data={healthCenterData?.universalPixel}
            isLoading={isLoading}
            icon={AreaIcon}
            title={t`C99 Universal Pixel`}
            instructions={t`Publish and place your C99 Universal Pixel`}
            link={ROUTES.pixels.path}
          >
            <HealthPodDetail label={t`Created On`}>
              {formatDate(healthCenterData?.universalPixel?.lastCreatedAt, 'MM/DD/YYYY')}
            </HealthPodDetail>
            <HealthPodDetail label={t`By`}>
              <HealthPodUser user={healthCenterData?.universalPixel?.createdBy} />
            </HealthPodDetail>
          </HealthPod>
        </div>
      </Page>
      {healthCenterData?.onboardingProgress === 1 && <Fireworks />}
    </>
  );
};

export default HealthCenter;
